import React, { useState, useEffect, useRef, useContext } from 'react'
import { Table, Button, Popconfirm, notification, Input, Space } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import TeamModal from './TeamModal'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_TEAMS } from '../graphql/Queries'
import { CREATE_TEAM, UPDATE_TEAM, DELETE_TEAM } from '../graphql/Mutations'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete, canUserSave } from 'utils/User'

const PAGE_SIZE = 10

const TeamTable = () => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)
  const canSave = canUserSave(userPermissions || currentUser, isClient)

  const { data, loading, refetch } = useQuery(GET_TEAMS)
  const [createTeam] = useMutation(CREATE_TEAM)
  const [updateTeam] = useMutation(UPDATE_TEAM)
  const [deleteTeam] = useMutation(DELETE_TEAM)

  const [modalVisible, setModalVisible] = useState(false)
  const [editingTeam, setEditingTeam] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [teams, setTeams] = useState([])
  const [filteredData, setFilteredData] = useState([])

  const searchInput = useRef(null)

  useEffect(() => {
    if (data && data.getTeams) {
      setTeams(data.getTeams)
      setFilteredData(data.getTeams) // default table data
    }
  }, [data])

  const handleAdd = () => {
    if (!canAdd) return
    setEditingTeam(null)
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    if (!canEdit) return
    setEditingTeam(record)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    if (!canDelete) return
    try {
      const res = await deleteTeam({ variables: { id } })
      if (res?.data?.deleteTeam) {
        if (res.data.deleteTeam?.success === true) {
          notification.success({ message: 'Team deleted' })
          refetch()
        } else {
          notification.info({
            message: res.data.deleteTeam?.message,
          })
        }
      }
      refetch()
    } catch (err) {
      notification.error({ message: 'Delete failed' })
    }
  }

  const handleModalOk = async (values) => {
    if (editingTeam && (!canEdit || !canSave)) return
    if (!editingTeam && (!canAdd || !canSave)) return
    try {
      if (editingTeam) {
        await updateTeam({ variables: { id: editingTeam.id, data: values } })
        notification.success({ message: 'Team updated' })
      } else {
        await createTeam({ variables: { data: values } })
        notification.success({ message: 'Team created' })
      }
      setModalVisible(false)
      refetch()
    } catch (err) {
      notification.error({ message: 'Operation failed' })
    }
  }

  // Column search helper
  const getColumnSearchProps = (dataIndex, renderFn) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys, confirm, dataIndex, renderFn)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() =>
              handleSearch(selectedKeys, confirm, dataIndex, renderFn)
            }
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
  })

  const handleSearch = (selectedKeys, confirm, dataIndex, renderFn) => {
    confirm()
    const value = selectedKeys[0] || ''

    const filtered = teams.filter((item) => {
      const fieldValue = renderFn ? renderFn(item) : item[dataIndex]
      return fieldValue
        ? fieldValue.toString().toLowerCase().includes(value.toLowerCase())
        : false
    })

    setFilteredData(filtered)
    setCurrentPage(1) // reset pagination to first page
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setFilteredData(teams)
    setCurrentPage(1)
  }

  const columns = [
    {
      title: 'Team Name',
      dataIndex: 'teamName',
      key: 'teamName',
      ...getColumnSearchProps('teamName'),
    },
    {
      title: 'Department',
      key: 'department',
      render: (_, record) => record.department?.departmentName || '',
      ...getColumnSearchProps(
        'department',
        (r) => r.department?.departmentName || '',
      ),
    },
    {
      title: 'Team Leader',
      key: 'teamLeader',
      render: (_, record) =>
        record.teamLeader
          ? `${record.teamLeader.firstName} ${
              record.teamLeader.lastName ? record.teamLeader.lastName : ''
            }`
          : '',
      ...getColumnSearchProps('teamLeader', (r) =>
        r.teamLeader
          ? `${r.teamLeader.firstName} ${
              r.teamLeader.lastName ? r.teamLeader.lastName : ''
            }`
          : '',
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {canEdit && (
            <EditOutlined
              style={{ marginRight: 12, color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleEdit(record)}
            />
          )}
          {canDelete && (
            <Popconfirm title='Delete?' onConfirm={() => handleDelete(record.id)}>
              <DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} />
            </Popconfirm>
          )}
        </>
      ),
    },
  ]

  // Pagination logic (works with filtered data)
  const paginatedData = filteredData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18 }}>Team settings</div>
        {canAdd && (
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add Team
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={paginatedData}
        rowKey='id'
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: filteredData.length,
          onChange: setCurrentPage,
        }}
      />
      <TeamModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        editingTeam={editingTeam}
        teams={teams}
      />
    </div>
  )
}

export default TeamTable
